import { format, parseISO, sub } from 'date-fns';
import { fork, put, take, takeEvery } from 'redux-saga/effects';

import { UnitDataListFormConditions } from '../domain/dai/types';
import { DataUnitDataListTransitionSearchParams } from '../domain/unitDataList/types';

import { dateTypes } from '../components/organisms/UnitDataListTransitionTable/dateTypes';

import { dataDaiOrderedDataSelector } from '../redux/server/dataDai';
import {
  DataUnitDataListTransitionActionCreators,
  DataUnitDataListTransitionActionTypes,
  dataUnitDataListTransitionSettingSelector,
} from '../redux/server/dataUnitDataListTransition';
import { dataColumnsSwappedSelector } from '../redux/server/dataUnitDataListTransitionColumnsSwappedData';
import { settingsOptionsDaiTransitionFieldsSelector } from '../redux/server/settingsOptionsDai';
import {
  ChangeUnitDataListTransitionDaiBanAction,
  ChangeUnitDataListTransitionFieldsAction,
  SearchUnitDataListTransitionAction,
  UnitDataListReportsSettingActionCreators,
  UnitDataListReportsSettingActionTypes,
  unitDataListReportsSearchConditionSelector,
  unitDataListTransitionColumnsOrderSelector,
  unitDataListTransitionDateRangeSelector,
  unitDataListTransitionSearchParamsSelector,
} from '../redux/ui/unitDataListReportsSetting';
import { TriggerUnitDataListTransitionSwapFieldsAction } from '../redux/ui/unitDataListReportsSetting';
import { customSelect } from '../utils/customSelect';
import { recalcColumnOrder } from '../utils/orderedCell';
import { resolveTransitionKiListOnDaiBanChange } from '../utils/unitDataListTransitionHalls';

/**
 * [台別データ推移]テーブルデータfetch用のラッパー関数。fetchしかつsearchParamsに値を設定する
 */
function* fetchDataUnitDataListTransitionSaga(
  searchParams: DataUnitDataListTransitionSearchParams
) {
  // 期間トグルの値を取得
  const dateRange = yield* customSelect(
    unitDataListTransitionDateRangeSelector
  );

  // action.payload.currentDateRange に応じて、対応する期間データを取得
  const selectedDateRange = dateTypes.find((date) => date.id === dateRange);

  const newStartDate = format(
    sub(parseISO(searchParams.endDate), {
      days: selectedDateRange
        ? selectedDateRange.number
        : dateTypes.at(0)?.number,
    }),
    'yyyy-MM-dd'
  );

  yield put(
    DataUnitDataListTransitionActionCreators.fetchDataUnitDataListTransitionAction(
      {
        ...searchParams,
        startDate: newStartDate,
      }
    )
  );
  yield take(
    DataUnitDataListTransitionActionTypes.FETCH_DATA_UNIT_DATA_LIST_TRANSITION_SUCCESS
  );
  const setting = yield* customSelect(
    dataUnitDataListTransitionSettingSelector
  );
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListTransitionSearchParamsAction(
      setting ?? { startDate: '', endDate: '', daiBan: [], kiList: [] }
    )
  );
}

/**
 * [台別データ推移]メインテーブルの検索条件を元に推移テーブルの検索条件を生成する
 *
 */
function generateTransitionSearchParams(
  kiCode: string,
  daiBan: number[],
  searchParams: UnitDataListFormConditions,
  transitionSearchParams: DataUnitDataListTransitionSearchParams,
  currentHall?: string[]
): DataUnitDataListTransitionSearchParams {
  return {
    // fieldsは前回の情報を引き継ぐ
    fields: transitionSearchParams.fields,
    // 平均合計行を表示しないパターンは今のところないためtrue固定
    containsAverage: true,
    // 推移テーブル上でのハイライト切り替えは不可能なのでtrue固定
    containsData: true,
    // それ以外は初回はメインテーブルの情報を利用する
    halls: currentHall ?? transitionSearchParams.halls,
    ...(searchParams.startDate && searchParams.startDate !== ''
      ? {
          startDate: searchParams.startDate,
        }
      : searchParams.ymdList
      ? {
          startDate: searchParams.ymdList[0],
        }
      : { startDate: new Date().toISOString().split('T')[0] }),
    ...(searchParams.endDate && searchParams.endDate !== ''
      ? {
          endDate: searchParams.endDate,
        }
      : searchParams.ymdList
      ? {
          endDate: searchParams.ymdList[searchParams.ymdList.length - 1],
        }
      : { endDate: new Date().toISOString().split('T')[0] }),
    excludeToday: searchParams.excludeToday,
    // 機種は毎回指定する
    kiList: [kiCode],
    daiBan: daiBan,
  };
}

/**
 * [台別データ推移]初回取得および、機種セレクタで機種変更時のデータ取得
 */
function* searchUnitDataListTransitionDataSaga(
  action: SearchUnitDataListTransitionAction
) {
  const { kiCode, daiBan, currentHall } = action.payload;
  const UnitDataListSearchParams = yield* customSelect(
    unitDataListReportsSearchConditionSelector
  );
  const UnitDataListTransitionSearchParams = yield* customSelect(
    unitDataListTransitionSearchParamsSelector
  );

  const generatedSearchParams = generateTransitionSearchParams(
    kiCode,
    daiBan,
    UnitDataListSearchParams,
    UnitDataListTransitionSearchParams,
    currentHall
  );

  yield fork(fetchDataUnitDataListTransitionSaga, {
    ...generatedSearchParams,
  });
}

/**
 * [台別データ推移]台番号変更時のデータ取得
 */
function* changeUnitDataListTransitionDaiBanSagas(
  action: ChangeUnitDataListTransitionDaiBanAction
) {
  // 現在の検索条件
  const searchParams = yield* customSelect(
    unitDataListTransitionSearchParamsSelector
  );

  const unitDataListData = yield* customSelect(dataDaiOrderedDataSelector);
  const daiBanColumnIndex = unitDataListData.columns.findIndex(
    (column) => column.name === '台番号'
  );
  const daiBan = action.payload.currentDaiBan.at(0);
  const kiList =
    daiBan !== undefined
      ? resolveTransitionKiListOnDaiBanChange({
          rows: unitDataListData.rows,
          daiBan,
          transitionKiList: searchParams.kiList,
          transitionHalls: searchParams.halls,
          daiBanColumnIndex,
        })
      : searchParams.kiList;

  // 変更後の台番号で再取得
  yield fork(fetchDataUnitDataListTransitionSaga, {
    ...searchParams,
    kiList,
    daiBan: action.payload.currentDaiBan,
  });
}

/**
 * [台別データ推移]期間指定トグル変更時のデータ取得
 */
function* changeUnitDataListTransitionDateRangeSagas() {
  // 現在の検索条件
  const searchParams = yield* customSelect(
    unitDataListTransitionSearchParamsSelector
  );
  // endDateがnullまたはundefinedの場合は、処理を停止またはエラーハンドリングを行う
  if (!searchParams.endDate) {
    throw new Error('endDate is required');
  }

  yield fork(fetchDataUnitDataListTransitionSaga, {
    ...searchParams,
  });
}

/**
 * [台別データ推移]表示項目変更時のデータ取得
 */
function* changeUnitDataListTransitionFieldsSagas(
  action: ChangeUnitDataListTransitionFieldsAction
) {
  const fields = action.payload.fields.map((item) => item.code);

  // 現在の検索条件
  const searchParams = yield* customSelect(
    unitDataListTransitionSearchParamsSelector
  );

  // 変更後の表示項目で再取得
  yield fork(fetchDataUnitDataListTransitionSaga, {
    ...searchParams,
    fields: fields,
  });

  // 新規追加された表示項目はスティッキー項目を除いて一律前に追加する処理
  const swappedData = yield* customSelect(dataColumnsSwappedSelector);
  const allFields = yield* customSelect(
    settingsOptionsDaiTransitionFieldsSelector
  );
  if (swappedData == null) {
    throw new Error('data is not found');
  }
  if (allFields == null) {
    throw new Error('settingsOptions is not found');
  }

  const columnsOrder = swappedData.data.columns
    .map((column) => column.code)
    .filter((code) => code != null) as string[];
  const difference =
    fields?.filter((field) => !columnsOrder.includes(field)) ?? [];

  const desiredOrder = allFields.map(({ code }) => code);
  const sortedDifference = [...difference].sort((a, b) => {
    if (desiredOrder == null) {
      return 0;
    }
    return desiredOrder.indexOf(a) < desiredOrder.indexOf(b) ? -1 : 1;
  });

  //fieldsが[]の時にデフォルトの表示項目が表示されるよう、空の配列を入れる
  const resultOrder = fields.length
    ? [...sortedDifference, ...columnsOrder]
    : [];

  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListTransitionColumnsOrderAction(
      resultOrder
    )
  );
}

/**
 * [台別データ推移]表示項目の並び順変更時のステート更新（データ取得はしない）
 */
function* triggerUnitDataListTransitionSwapFieldsSaga(
  action: TriggerUnitDataListTransitionSwapFieldsAction
) {
  const { draggedId, droppedId } = action.payload;

  const responseData = yield* customSelect(dataColumnsSwappedSelector);
  if (responseData == null) {
    return;
  }

  const currentOrder = yield* customSelect(
    unitDataListTransitionColumnsOrderSelector
  );

  const ordered = recalcColumnOrder(
    currentOrder.length === 0
      ? responseData.data.columns
      : currentOrder.map((code) => ({ code })),
    draggedId,
    droppedId
  );

  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListTransitionColumnsOrderAction(
      ordered
    )
  );
}

function* handleSaga() {
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.SEARCH_UNIT_DATA_LIST_TRANSITION,
    searchUnitDataListTransitionDataSaga
  );

  yield takeEvery(
    UnitDataListReportsSettingActionTypes.CHANGE_UNIT_DATA_LIST_TRANSITION_DAI_BAN,
    changeUnitDataListTransitionDaiBanSagas
  );

  yield takeEvery(
    UnitDataListReportsSettingActionTypes.CHANGE_UNIT_DATA_LIST_TRANSITION_DATE_RANGE,
    changeUnitDataListTransitionDateRangeSagas
  );

  yield takeEvery(
    UnitDataListReportsSettingActionTypes.CHANGE_UNIT_DATA_LIST_TRANSITION_FIELDS,
    changeUnitDataListTransitionFieldsSagas
  );

  yield takeEvery(
    UnitDataListReportsSettingActionTypes.TRIGGER_UNIT_DATA_LIST_TRANSITION_SWAP_FIELDS,
    triggerUnitDataListTransitionSwapFieldsSaga
  );
}

export function* unitDataListTransitionSaga() {
  yield fork(handleSaga);
}
